/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Port } from "../PortData";
import { GenericTag } from "@ui-components/Tag/GenericTag";

export interface PortViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  port: Port | null;
  onClose: () => void;
}

export const PortViewModal: React.FC<PortViewModalProps> = ({
  isOpen,
  isLoading,
  port,
  onClose,
}) => {
  // Determine if port is virtual (has config object)
  const isVirtual = port ? !!(port as any).config : false;
  
  // Parse crossconnect details if available (for both physical and virtual ports)
  let crossconnectType = "";
  let crossconnectSpeed = "";
  let crossconnectId = "";
  
  if (port && (port as any).config?.crossConnect) {
    const crossConnect = (port as any).config.crossConnect;
    crossconnectId = crossConnect.id || "";
    
    // Parse details: "SMF, 10G, KIO XC ID:" -> type: "SMF", speed: "10G"
    if (crossConnect.details) {
      const detailsParts = crossConnect.details.split(",").map((part: string) => part.trim());
      if (detailsParts.length >= 1) {
        crossconnectType = detailsParts[0]; // First part is type
      }
      if (detailsParts.length >= 2) {
        crossconnectSpeed = detailsParts[1]; // Second part is speed
      }
    }
  }
  
  // Handle tags - can be array or string
  let tagsDisplay = "";
  if (port) {
    if (Array.isArray((port as any).tag)) {
      tagsDisplay = (port as any).tag.join(", ");
    } else if (typeof (port as any).tag === "string") {
      tagsDisplay = (port as any).tag;
    }
  }

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="View Port Details"
      customWidth="max-w-6xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 10 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="45%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        port && (
          <div className="space-y-6">
            {/* Tenant and Port Type Selection */}
            <GenericCard isCollapsible={false} className="p-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col col-span-2 space-y-2">
                  <label className="text-sm font-medium">Tenant</label>
                  <GenericInput
                    label=""
                    value={port.tenant?.nameDisplay || port.tenant?.Name || ""}
                    readOnly
                  />
                </div>
                <div className="flex flex-col space-y-2 items-end">
                  <label className="text-sm font-medium text-left">Port type</label>
                  <GenericTag
                    label={isVirtual ? "Virtual" : "Physical"}
                    variant="neutral"
                    size="medium"
                  />
                </div>
              </div>
            </GenericCard>

            {/* Location Selection - Only for Physical Ports */}
            {!isVirtual && port.datacenter && (
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Location Selection
                </h3>
                <div className="flex flex-col space-y-4">
                  {/* First row: Data Center and Hardware in 2 columns */}
                  <div className="grid grid-cols-2 gap-4">
                    <GenericInput
                      label="Data Center"
                      value={port.datacenter.displayName || port.datacenter.name || ""}
                      readOnly
                    />
                    <GenericInput
                      label="Hardware ID"
                      value={port.hardwareId || (port as any).hardwareId || "N/A"}
                      readOnly
                    />
                  </div>
                  {/* Second row: Port - Full width */}
                  <div className="flex flex-col space-y-2">
                    <GenericInput
                      label="Port"
                      value={port.name || port.portName || "N/A"}
                      readOnly
                    />
                  </div>
                </div>
              </GenericCard>
            )}

            {/* Provider Data - Only for Virtual Ports */}
            {isVirtual && (
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Provider Data
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <GenericInput
                    label="Provider"
                    value={port.provider || "N/A"}
                    readOnly
                  />
                  <GenericInput
                    label="Provider Account"
                    value={(port as any).providerAccount || "N/A"}
                    readOnly
                  />
                  <GenericInput
                    label="Provider Id"
                    value={(port as any).providerId || "N/A"}
                    readOnly
                  />
                </div>
              </GenericCard>
            )}

            {/* Port Information Section - Common for both Physical and Virtual */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Port Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="Subscription ID"
                  value={(port as any).subscriptionId || (port as any).subid || port.portId || String(port.id || "")}
                  readOnly
                />
                <GenericInput
                  label="Port Name"
                  value={port.name || port.portName || ""}
                  readOnly
                />
                <GenericInput
                  label="Tags"
                  value={tagsDisplay || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Capacity (Mbps)"
                  value={port.capacityMbps ? String(port.capacityMbps) : port.portType || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Created At"
                  value={port.createdAt ? new Date(port.createdAt).toLocaleString() : "N/A"}
                  readOnly
                />
              </div>
              {/* Status Display */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <label className="text-sm font-medium text-semantic-text-secondary">
                  Status:
                </label>
                <GenericTag
                  label={port.isDraft !== undefined ? (port.isDraft ? "Draft" : "Active") : (port.portStatus || "Unknown")}
                  variant={port.isDraft !== undefined ? (port.isDraft ? "warning" : "success") : "default"}
                  size="medium"
                />
              </div>
            </GenericCard>

            {/* Crossconnect Information - For both Physical and Virtual Ports */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Crossconnect Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="Type"
                  value={crossconnectType || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Speed"
                  value={crossconnectSpeed || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Id"
                  value={crossconnectId || "N/A"}
                  readOnly
                />
              </div>
            </GenericCard>

            {/* Tenant Information Section */}
            {port.tenant && (
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Tenant Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <GenericInput
                    label="Tenant Name"
                    value={port.tenant.nameDisplay || port.tenant.Name || ""}
                    readOnly
                  />
                  <GenericInput
                    label="Chargebee Customer ID"
                    value={port.tenant.chargebeeCustomerId || "N/A"}
                    readOnly
                  />
                </div>
              </GenericCard>
            )}
          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};

